import axios from 'axios';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { API_URLs, BASE_URL } from './http.service';
import { clearAccessToken, emitAuthFailure, getAccessToken, setAccessToken } from './token.store';

/**
 * Best human-readable message for a failed request.
 *
 * Each source is used only if it actually YIELDS text. That is the whole point:
 * ApiError always serializes `errors: []`, so an earlier version — which
 * returned the joined array as soon as it was an array — produced an empty
 * string for every ordinary API error and the user got a blank red toast with
 * no reason in it. Validation detail, when present, still wins over the
 * generic message.
 */
const getApiErrorMessage = (error) => {
  const payload = error?.response?.data;

  if (typeof payload?.errors === 'string' && payload.errors.trim()) return payload.errors;

  if (Array.isArray(payload?.errors)) {
    const joined = payload.errors
      .map((item) => item?.message || item?.msg || (typeof item === 'string' ? item : ''))
      .filter(Boolean)
      .join(', ');
    if (joined) return joined;
  }

  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;
  if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error;

  // Multer rejects an oversized file before any handler runs, so there is no
  // ApiError envelope at all — only the status.
  if (error?.response?.status === 413) return 'That file is too large to upload.';

  return error?.message || 'Request failed. Please try again.';
};

const showApiErrorToast = (error, fallbackMessage) => {
  toast.error(fallbackMessage || getApiErrorMessage(error), {
    id: `api-error-${error?.config?.url || 'request'}`,
  });
};

const useAxiosInstance = () => {
  const axiosInstance = useMemo(() => {
    const instance = axios.create({ baseURL: BASE_URL, withCredentials: true });
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
      failedQueue.forEach((prom) => {
        if (error) prom.reject(error);
        else prom.resolve(token);
      });
      failedQueue = [];
    };

    instance.interceptors.request.use((config) => {
      const token = getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            try {
              const token = await new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              });
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            } catch (err) {
              return Promise.reject(err);
            }
          }

          originalRequest._retry = true;
          isRefreshing = true;

          return new Promise((resolve, reject) => {
            axios
              .post(API_URLs.refreshToken, {}, { withCredentials: true })
              .then(({ data }) => {
                const newAccess = data?.data?.accessToken;
                if (newAccess) setAccessToken(newAccess);
                processQueue(null, newAccess);
                originalRequest.headers.Authorization = `Bearer ${newAccess}`;
                resolve(instance(originalRequest));
              })
              .catch((err) => {
                processQueue(err, null);
                // The refresh cookie is gone, expired or rejected — this session
                // is over. Drop the stale access token and tell AuthProvider, or
                // context keeps reporting isLoggedIn while every call 401s.
                clearAccessToken();
                emitAuthFailure();
                // Silent on the boot probe: an anonymous first visit takes this
                // exact path, and "your session could not be refreshed" is a
                // confusing thing to show someone who never had one.
                if (!originalRequest._silent) {
                  showApiErrorToast(
                    err,
                    'Your session could not be refreshed. Please sign in again if this continues.'
                  );
                }
                reject(err);
              })
              .finally(() => {
                isRefreshing = false;
              });
          });
        }
        if (!originalRequest?._silent) showApiErrorToast(error);
        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  return axiosInstance;
};

// Default export — used as: const api = useAPI()
const useAPI = () => {
  const axiosInstance = useAxiosInstance();

  return useMemo(
    () => ({
      login: async (body) => {
        const { data } = await axios.post(API_URLs.login, body, {
          withCredentials: true,
        });
        return data;
      },
      register: async (body) => {
        const { data } = await axios.post(API_URLs.register, body, {
          withCredentials: true,
        });
        return data;
      },
      // `_silent` on both: signing out and probing for an existing session are
      // the two calls whose failure the user should never see as a red toast.
      // Logout clears client state either way, and a failed probe just means
      // "not signed in".
      logout: async () => {
        const { data } = await axiosInstance.post(API_URLs.logout, {}, { _silent: true });
        return data;
      },
      getCurrentUser: async ({ silent = false } = {}) => {
        const { data } = await axiosInstance.get(API_URLs.currentUser, { _silent: silent });
        return data;
      },
      changePassword: async (body) => {
        const { data } = await axiosInstance.post(API_URLs.changePassword, body);
        return data;
      },
      forgotPassword: async (body) => {
        const { data } = await axiosInstance.post(API_URLs.forgotPassword, body);
        return data;
      },
      verifyOtp: async (body) => {
        const { data } = await axiosInstance.post(API_URLs.verifyOtp, body);
        return data;
      },
      getAllUsers: async (params = {}) => {
        const { data } = await axiosInstance.get(API_URLs.users, { params });
        return data;
      },
      addNewUser: async (body) => {
        const payload = { ...body, roleId: body.roleId || body.role };
        const { data } = await axiosInstance.post(API_URLs.users, payload);
        return data;
      },
      editUser: async (userId, body) => {
        const payload = { ...body, roleId: body.roleId || body.role };
        const { data } = await axiosInstance.patch(`${API_URLs.users}/${userId}`, payload);
        return data;
      },
      deleteUser: async (userId) => {
        const { data } = await axiosInstance.delete(`${API_URLs.users}/${userId}`);
        return data;
      },
      /**
       * Generic GET for the fulfillment cockpit.
       *
       * One method rather than eleven near-identical ones: every fulfillment
       * endpoint is a read that differs only by URL and query params, and the
       * URLs are already named constants in http.service. The hooks in
       * fulfillment.service.js are what callers actually use.
       */
      getFulfillment: async (url, params = {}) => {
        const { data } = await axiosInstance.get(url, { params });
        return data;
      },
      postFulfillment: async (url, body = {}) => {
        const { data } = await axiosInstance.post(url, body);
        return data;
      },
      downloadFulfillmentFile: async (url) => {
        const response = await axiosInstance.get(url, { responseType: 'blob' });
        const disposition = response.headers['content-disposition'] || '';
        const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || 'fulfillment-export';
        const objectUrl = URL.createObjectURL(response.data);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
      },
      /**
       * Replace one fulfillment source file. `body` is a FormData holding the
       * workbook under the field name `file`.
       *
       * No explicit Content-Type: the browser must set it itself so the
       * multipart boundary is included. Setting 'multipart/form-data' by hand
       * omits the boundary and multer rejects the request.
       */
      uploadFulfillmentFile: async (kind, body) => {
        const { data } = await axiosInstance.post(`${API_URLs.fulfillmentUploads}/${kind}`, body);
        return data;
      },
      rollbackFulfillmentFile: async (kind) => {
        const { data } = await axiosInstance.post(
          `${API_URLs.fulfillmentUploads}/${kind}/rollback`
        );
        return data;
      },
      /**
       * Re-read a warehouse-served source from Fabric. This is the action that
       * replaces Upload/Replace on those cards — there is no file to send.
       */
      refreshFulfillmentSource: async (kind) => {
        const { data } = await axiosInstance.post(`${API_URLs.fulfillmentUploads}/${kind}/refresh`);
        return data;
      },
      // Row actions — mirror the backend POST/DELETE /orders/:orderNumber/...
      // routes. The API methods are non-hook (plain async functions) because
      // the calls are user-triggered rather than subscribed; they are not
      // a stateful resource.
      escalateOrder: async (orderNumber) => {
        const { data } = await axiosInstance.post(API_URLs.orderEscalate(orderNumber));
        return data;
      },
      clearOrderEscalation: async (orderNumber) => {
        const { data } = await axiosInstance.delete(API_URLs.orderEscalate(orderNumber));
        return data;
      },
      snoozeOrder: async (orderNumber, until) => {
        const { data } = await axiosInstance.post(API_URLs.orderSnooze(orderNumber), { until });
        return data;
      },
    }),
    [axiosInstance]
  );
};

export default useAPI;
